import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { KeyIcon, LockIcon, ShieldCheckIcon } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { FieldError } from '@/components/ui/field-error';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ManageRolesContent } from '@/content/ManageRolesContent';
import { slugifyRoleKey } from '@/lib/services/roles';
import type { RoleFormProps, RoleFormValues } from '@/lib/types/roles.types';
import { cn } from '@/lib/utils';
import {
  DEFAULT_ROLE_COLOR,
  ROLE_COLOR_OPTIONS,
} from '@/lib/utils/roles.utils';

const initialValues: RoleFormValues = {
  displayName: '',
  description: '',
  chipColor: DEFAULT_ROLE_COLOR,
};

export function RoleForm({
  role,
  isSubmitting,
  submitError,
  onSubmit,
  onCancel,
}: RoleFormProps) {
  const [values, setValues] = useState<RoleFormValues>(
    role
      ? {
          displayName: role.display_name,
          description: role.description ?? '',
          chipColor: role.chip_color ?? DEFAULT_ROLE_COLOR,
        }
      : initialValues
  );
  const [submitted, setSubmitted] = useState(false);

  const isEditing = !!role;
  const isSystem = !!role?.is_system;
  const content = ManageRolesContent.form;
  const keyPreview = useMemo(
    () => slugifyRoleKey(values.displayName),
    [values.displayName]
  );
  const displayNameError =
    submitted && !values.displayName.trim()
      ? content.validation.missingName
      : null;
  const keyError =
    submitted && values.displayName.trim() && !keyPreview && !isEditing
      ? content.validation.emptyKey
      : null;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitted(true);

    if (isSystem || !values.displayName.trim() || (!isEditing && !keyPreview)) {
      return;
    }

    await onSubmit({
      displayName: values.displayName,
      description: values.description.trim() || null,
      chipColor: values.chipColor,
    });
  };

  const title = isSystem
    ? content.systemTitle
    : isEditing
      ? content.editTitle
      : content.createTitle;
  const description = isSystem
    ? content.systemDescription
    : isEditing
      ? content.editDescription
      : content.createDescription;

  return (
    <main aria-labelledby="role-form-title" className="bg-slate-700">
      <div className="mx-auto w-full max-w-2xl px-4 pb-8 pt-3">
        <div className="mb-5 max-w-xl">
          <h1
            id="role-form-title"
            className="font-field-74 text-h1 leading-tight text-slate-50"
          >
            {title}
          </h1>
          <p className="mt-2 text-body text-slate-400">{description}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {submitError && (
            <div
              role="alert"
              className="rounded-lg border border-red-400 bg-red-500/10 px-4 py-3 text-body text-red-100"
            >
              {submitError}
            </div>
          )}

          <div className="space-y-2">
            <Label
              className="font-field-42 text-[11px] uppercase text-slate-400"
              htmlFor="role-name"
            >
              {content.fields.displayName}
              <span className="text-red-400"> *</span>
            </Label>
            <Input
              id="role-name"
              value={values.displayName}
              onChange={(event) =>
                setValues((current) => ({
                  ...current,
                  displayName: event.target.value,
                }))
              }
              placeholder={content.fields.displayNamePlaceholder}
              aria-invalid={!!displayNameError}
              disabled={isSubmitting || isSystem}
              className={cn(
                'h-12 rounded-lg border-slate-550 bg-slate-600 text-slate-50 placeholder:text-slate-500 focus-visible:border-purple-300 focus-visible:ring-purple-300',
                displayNameError && 'border-red-500'
              )}
            />
            {displayNameError && <FieldError errors={[displayNameError]} />}
          </div>

          <div
            className={cn(
              'rounded-lg p-4',
              isEditing
                ? 'border border-dashed border-slate-500 bg-slate-700/70'
                : 'border border-slate-550 bg-slate-600'
            )}
          >
            <div className="mb-2 flex items-center justify-between gap-3">
              <span className="flex items-center gap-2 font-field-42 text-[11px] uppercase text-slate-400">
                <KeyIcon size={14} weight="bold" />
                {isEditing ? content.fields.key : content.fields.keyPreview}
              </span>
              {isEditing && (
                <span className="flex items-center gap-1 text-small text-slate-500">
                  <LockIcon size={12} weight="bold" />
                  Locked
                </span>
              )}
            </div>
            <code className="min-h-6 min-w-0 break-all font-mono text-body text-purple-300">
              {isEditing ? role?.key : keyPreview || content.fields.emptyKey}
            </code>
            <p className="mt-2 flex items-center gap-2 text-small text-slate-400">
              <LockIcon size={12} weight="bold" className="mt-0.5 shrink-0" />
              <span>{isEditing ? content.lockedKeyHelp : content.keyHelp}</span>
            </p>
            {keyError && (
              <div className="mt-2">
                <FieldError errors={[keyError]} />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label className="font-field-42 text-[11px] uppercase text-slate-400">
              {content.fields.color}
            </Label>
            <div
              className="flex flex-wrap gap-2"
              role="radiogroup"
              aria-label={content.fields.color}
            >
              {ROLE_COLOR_OPTIONS.map((option) => {
                const selected = values.chipColor === option.token;
                return (
                  <button
                    key={option.token}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    aria-label={option.label}
                    disabled={isSubmitting || isSystem}
                    onClick={() =>
                      setValues((current) => ({
                        ...current,
                        chipColor: option.token,
                      }))
                    }
                    className={cn(
                      'flex h-10 w-10 items-center justify-center rounded-full border-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-300 disabled:cursor-not-allowed disabled:opacity-50',
                      selected ? 'border-slate-50' : 'border-transparent'
                    )}
                  >
                    <span
                      className={cn(
                        'h-6 w-6 rounded-full',
                        option.swatchClassName
                      )}
                    />
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-2">
            <Label
              className="font-field-42 text-[11px] uppercase text-slate-400"
              htmlFor="role-description"
            >
              {content.fields.description}
              <span className="text-slate-500"> · optional</span>
            </Label>
            <Textarea
              id="role-description"
              value={values.description}
              onChange={(event) =>
                setValues((current) => ({
                  ...current,
                  description: event.target.value,
                }))
              }
              placeholder={content.fields.descriptionPlaceholder}
              disabled={isSubmitting || isSystem}
              className="min-h-20 rounded-lg border-slate-550 bg-slate-600 text-slate-50 placeholder:text-slate-500 focus-visible:border-purple-300 focus-visible:ring-purple-300"
            />
          </div>

          {isSystem && (
            <div className="flex gap-3 rounded-lg border border-blue-300/40 bg-blue-300/10 p-3 text-body text-blue-100">
              <ShieldCheckIcon
                size={20}
                weight="bold"
                className="mt-0.5 shrink-0 text-blue-300"
              />
              <p>{content.systemDescription}</p>
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              size="lg"
              disabled={isSubmitting}
              onClick={onCancel}
              className="h-12 flex-1 border-slate-550 bg-transparent text-slate-50 hover:bg-slate-600"
            >
              {isSystem
                ? ManageRolesContent.actions.close
                : ManageRolesContent.actions.cancel}
            </Button>
            {!isSystem && (
              <Button
                type="submit"
                size="lg"
                disabled={isSubmitting}
                className="h-12 flex-1 bg-purple-300 text-slate-700 hover:bg-purple-400"
              >
                {isSubmitting
                  ? isEditing
                    ? content.saving
                    : content.creating
                  : isEditing
                    ? content.editConfirm
                    : content.createConfirm}
              </Button>
            )}
          </div>
        </form>
      </div>
    </main>
  );
}
